import * as Sentry from '@sentry/react';
import { ExpandedState } from '@tanstack/react-table';
import { useState } from 'react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../../contexts/data-context/use-data';
import { useUser } from '../../../../contexts/user-context/use-user';
import { ErrorSection } from '../../../../design-system/error-section/error-section';
import { Inline } from '../../../../design-system/inline/inline';
import { AiEnrichmentLoading } from '../../../../design-system/loading/ai-enrichment-loading';
import { LoadingSection } from '../../../../design-system/loading/loading';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView } from '../../../../types/entity-types';
import { postSwrFetcher } from '../../../../utils/post-swr-fetcher';
import { getReportingData } from '../../utils/get-reporting-data';
import { ChangeVendorCategoryModal } from './change-vendor-category-modal';
import { CreateCategoryFormFormValues } from './create-category-form';
import { Level1CategoryChangeModal } from './level-1-category-change-modal';
import { ReportingDataTable } from './reporting-data-table';

const DEFAULT_CATEGORY_ICON = 'fa-tag';
const CREATE_CATEGORY_ENDPOINT = '/api/categories';

type CreateCategoryRequestType = {
  level1: string;
  level2: string;
  icon: `fa-${string}`;
};

export const ReportingDataTableWrapper = () => {
  const { authenticatedFetch } = useUser();
  const [isLevel1SelectorOpen, setIsLevel1SelectorOpen] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | undefined>();
  const [selectedCategory, setSelectedCategory] = useState<{ id: string; level1?: string } | undefined>();
  const [isUpdatingVendorCategory, setIsUpdatingVendorCategory] = useState(false);
  const { data, isLoading: isLoadingData, error: dataError, mutations, meta } = useData();
  // Expanded and parent row state are kept in this component so it persists when data is re-fetched after the vendor category is updated
  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const [rowIdToScrollTo, setRowIdToScrollTo] = useState<string | undefined>();
  const {
    trigger: triggerCreateCategory,
    isMutating: isCreatingCategory,
    error: createCategoryError,
  } = useSWRMutation<CategoryView, Error, string, CreateCategoryRequestType>(CREATE_CATEGORY_ENDPOINT, postSwrFetcher);

  if (isLoadingData) {
    return <LoadingSection text="Loading data..." />;
  }

  if (!data) {
    return <ErrorSection error={dataError} />;
  }

  const { categories, vendors } = data;
  const reportingData = getReportingData({ categories, vendors, baseCurrency: data.baseCurrency });

  const onMoveVendor = ({ vendorId, parentRowId }: { vendorId: string; parentRowId: string }) => {
    setSelectedVendorId(vendorId);
    setRowIdToScrollTo(parentRowId);
  };

  const onMoveCategory = ({ id, parentRowId, level1 }: { id: string; parentRowId: string; level1?: string }) => {
    setSelectedCategory({ id, level1 });
    setIsLevel1SelectorOpen(true);
    setRowIdToScrollTo(parentRowId);
  };

  const onSelectCategory = async (categoryId: string) => {
    if (!selectedVendorId) {
      return;
    }

    setIsUpdatingVendorCategory(true);

    try {
      const patchOperation = [
        {
          id: selectedVendorId,
          patch: [
            {
              op: 'replace',
              path: '/categoryId',
              value: categoryId,
            },
          ],
        },
      ];

      const response = await authenticatedFetch('/api/entities/by-type/vendor', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patchOperation),
      });

      if (!response.ok) {
        throw new Error('Failed to update vendor category');
      }

      toast.success('Vendor category updated successfully');

      mutations.handleIncrementalUpdates();

      onCloseVendorCategoryModal();
    } catch (error) {
      toast.error('Failed to update vendor category');

      Sentry.captureException(error);
    } finally {
      setIsUpdatingVendorCategory(false);
    }
  };

  const onCreateCategory = async (formValues: CreateCategoryFormFormValues) => {
    const { level1Category, level2Category } = formValues;
    try {
      const category = await triggerCreateCategory({
        level1: level1Category,
        level2: level2Category,
        icon: DEFAULT_CATEGORY_ICON,
      });

      // Moves the selected vendor to the newly created category
      onSelectCategory(category.id);

      toast.success('Category created successfully');

      mutations.handleIncrementalUpdates();

      onCloseVendorCategoryModal();
    } catch (error) {
      toast.error('Failed to create category');

      Sentry.captureException(error, {
        extra: {
          error,
          swrError: createCategoryError,
        },
      });
    }
  };

  const onCloseVendorCategoryModal = () => {
    setSelectedVendorId(undefined);
  };

  const onSelectLevel1 = async (level1: string) => {
    if (!selectedCategory?.id) {
      return;
    }

    try {
      const patchOperation = [
        {
          id: selectedCategory.id,
          patch: [
            {
              op: 'replace',
              path: '/level1',
              value: level1,
            },
          ],
        },
      ];

      const response = await authenticatedFetch('/api/entities/by-type/category', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patchOperation),
      });

      if (!response.ok) {
        throw new Error('Failed to update category');
      }

      toast.success('Category updated successfully');

      mutations.mutateAll();

      setIsLevel1SelectorOpen(false);
    } catch (error) {
      toast.error('Failed to update category');

      Sentry.captureException(error);
    }
  };

  if (reportingData.data.length === 0) {
    return (
      <Stack space={3} align="stretch" className="mt-3">
        {meta?.isAiEnrichmentInProgress && (
          <AiEnrichmentLoading description="Our AI engine is enriching your reporting data..." />
        )}

        <Inline justify="center" className="mt-10">
          <p className="text-base text-seapoint-gray-text">No reporting data available</p>
        </Inline>
      </Stack>
    );
  }

  return (
    <div className="flex flex-col gap-5 py-4">
      {meta?.isAiEnrichmentInProgress && (
        <div className="px-4">
          <AiEnrichmentLoading description="Our AI engine is enriching your reporting data..." />
        </div>
      )}

      <ReportingDataTable
        data={reportingData}
        onMoveVendor={onMoveVendor}
        onMoveCategory={onMoveCategory}
        expandedState={expandedState}
        setExpandedState={setExpandedState}
        initialScrollToRowId={rowIdToScrollTo}
        categories={categories}
        baseCurrency={data.baseCurrency}
      />

      {selectedVendorId && (
        <ChangeVendorCategoryModal
          isOpen={!!selectedVendorId}
          vendor={vendors.find((v) => v.id === selectedVendorId)}
          categories={categories}
          isLoading={isUpdatingVendorCategory || isCreatingCategory}
          onClose={onCloseVendorCategoryModal}
          onChangeCategory={onSelectCategory}
          onCreateCategory={onCreateCategory}
        />
      )}

      {selectedCategory && (
        <Level1CategoryChangeModal
          isOpen={isLevel1SelectorOpen}
          onClose={() => {
            setIsLevel1SelectorOpen(false);
            setSelectedCategory(undefined);
          }}
          onSelectLevel1Category={async (l1) => {
            await onSelectLevel1(l1);

            setIsLevel1SelectorOpen(false);
            setSelectedCategory(undefined);
          }}
          // Unique level1 categories excluding the current level1 category
          level1List={[...new Set(categories.map((c) => c.level1).filter((l1) => l1 !== selectedCategory?.level1))]}
          rowLevel1={selectedCategory?.level1}
        />
      )}
    </div>
  );
};
